import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  FormControlLabel,
  Checkbox,
  Grid,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import WarningIcon from "@material-ui/icons/Warning";
import InfoIcon from "@material-ui/icons/Info";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import toastUtils from "../../utils/toastUtils";

const useStyles = makeStyles((theme) => ({
  root: {
    minWidth: 600,
  },
  uploadArea: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    textAlign: "center",
    cursor: "pointer",
    transition: "border-color 0.3s",
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
    "&.dragover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
  },
  fileInput: {
    display: "none",
  },
  uploadIcon: {
    fontSize: 48,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  progressContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  resultItem: {
    padding: theme.spacing(1),
  },
  successIcon: {
    color: theme.palette.success.main,
  },
  errorIcon: {
    color: theme.palette.error.main,
  },
  warningIcon: {
    color: theme.palette.warning.main,
  },
  infoIcon: {
    color: theme.palette.info.main,
  },
}));

const SDRLeadUploadModal = ({ open, onClose, onSuccess }) => {
  const classes = useStyles();
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [validateEmails, setValidateEmails] = useState(true);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    if (open) {
      fetchCampaigns();
    }
  }, [open]);

  const fetchCampaigns = async () => {
    try {
      const { data } = await api.get("/sdr/campaigns");
      setCampaigns(data.data.filter(c => c.status !== "completed"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleFileSelect = (selectedFile) => {
    const allowedTypes = [
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    if (!allowedTypes.includes(selectedFile.type) &&
        !selectedFile.name.endsWith('.csv') &&
        !selectedFile.name.endsWith('.xls') &&
        !selectedFile.name.endsWith('.xlsx')) {
      toastUtils.error(i18n.t("sdr.upload.invalidFileType"));
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      toastUtils.error(i18n.t("sdr.upload.fileTooLarge"));
      return;
    }

    setFile(selectedFile);
    setUploadResult(null);
  };

  const handleFileInputChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);

    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleUpload = async () => {
    if (!file || !selectedCampaign) {
      toastUtils.error(i18n.t("sdr.upload.validation.missingFields"));
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("skipDuplicates", skipDuplicates);
      formData.append("validateEmails", validateEmails);

      const { data } = await api.post(
        `/sdr/campaigns/${selectedCampaign}/upload-leads`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setUploadResult(data.data);
      toastUtils.success(data.message);

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setUploadResult(null);
    setSelectedCampaign("");
    setSkipDuplicates(true);
    setValidateEmails(true);
    setDragOver(false);
    onClose();
  };

  const getResultIcon = (type) => {
    switch (type) {
      case "success":
        return <CheckCircleIcon className={classes.successIcon} />;
      case "error":
        return <ErrorIcon className={classes.errorIcon} />;
      case "warning":
        return <WarningIcon className={classes.warningIcon} />;
      default:
        return <InfoIcon className={classes.infoIcon} />;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{i18n.t("sdr.upload.title")}</DialogTitle>
      <DialogContent className={classes.root}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>{i18n.t("sdr.upload.selectCampaign")}</InputLabel>
              <Select
                value={selectedCampaign}
                onChange={(e) => setSelectedCampaign(e.target.value)}
                disabled={uploading}
              >
                {campaigns.map((campaign) => (
                  <MenuItem key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <input
              accept=".csv,.xls,.xlsx"
              className={classes.fileInput}
              id="file-upload"
              type="file"
              onChange={handleFileInputChange}
              disabled={uploading}
            />
            <label htmlFor="file-upload">
              <div
                className={`${classes.uploadArea} ${dragOver ? "dragover" : ""}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <CloudUploadIcon className={classes.uploadIcon} />
                <Typography variant="h6" gutterBottom>
                  {file ? file.name : i18n.t("sdr.upload.dropFileHere")}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {i18n.t("sdr.upload.supportedFormats")}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {i18n.t("sdr.upload.maxFileSize")}
                </Typography>
              </div>
            </label>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              {i18n.t("sdr.upload.options")}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={skipDuplicates}
                  onChange={(e) => setSkipDuplicates(e.target.checked)}
                  disabled={uploading}
                />
              }
              label={i18n.t("sdr.upload.skipDuplicates")}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={validateEmails}
                  onChange={(e) => setValidateEmails(e.target.checked)}
                  disabled={uploading}
                />
              }
              label={i18n.t("sdr.upload.validateEmails")}
            />
          </Grid>

          {uploading && (
            <Grid item xs={12}>
              <Box className={classes.progressContainer}>
                <Typography variant="body2" gutterBottom>
                  {i18n.t("sdr.upload.processing")}
                </Typography>
                <LinearProgress />
              </Box>
            </Grid>
          )}

          {uploadResult && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {i18n.t("sdr.upload.results")}
              </Typography>

              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                <Chip
                  icon={getResultIcon("success")}
                  label={`${uploadResult.importedLeads} ${i18n.t("sdr.upload.imported")}`}
                  color="primary"
                />
                <Chip
                  icon={getResultIcon("warning")}
                  label={`${uploadResult.skippedRows} ${i18n.t("sdr.upload.skipped")}`}
                  color="default"
                />
                <Chip
                  icon={getResultIcon("info")}
                  label={`${uploadResult.duplicates} ${i18n.t("sdr.upload.duplicates")}`}
                  color="default"
                />
                <Chip
                  icon={getResultIcon("error")}
                  label={`${uploadResult.errors.length} ${i18n.t("sdr.upload.errors")}`}
                  color="secondary"
                />
              </Box>

              {uploadResult.errors.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    {i18n.t("sdr.upload.errorDetails")}
                  </Typography>
                  <List dense>
                    {uploadResult.errors.slice(0, 10).map((error, index) => (
                      <ListItem key={index} className={classes.resultItem}>
                        <ListItemIcon>
                          <ErrorIcon className={classes.errorIcon} />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${i18n.t("sdr.upload.row")} ${error.row}: ${error.error}`}
                          secondary={error.data ? JSON.stringify(error.data) : null}
                        />
                      </ListItem>
                    ))}
                    {uploadResult.errors.length > 10 && (
                      <ListItem>
                        <ListItemText
                          primary={i18n.t("sdr.upload.moreErrors", {
                            count: uploadResult.errors.length - 10,
                          })}
                        />
                      </ListItem>
                    )}
                  </List>
                </>
              )}
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{i18n.t("common.close")}</Button>
        {!uploadResult && (
          <Button
            onClick={handleUpload}
            color="primary"
            variant="contained"
            disabled={!file || !selectedCampaign || uploading}
            startIcon={<CloudUploadIcon />}
          >
            {uploading ? i18n.t("common.loading") : i18n.t("sdr.upload.upload")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SDRLeadUploadModal;